var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * React Query hooks for Rich Content Template resources
 */
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRichContentResources, updateRichContentResource } from '../services/RichContentService';
export const TEMPLATE_RESOURCES_QUERY_KEY = ['template-resources'];
/**
 * Hook to fetch all rich content resources
 * @returns Query result with resources data, loading state, and error
 */
export function useResources() {
    return useQuery({
        queryKey: TEMPLATE_RESOURCES_QUERY_KEY,
        queryFn: getRichContentResources,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
    });
}
/**
 * Hook to update a rich content resource
 * @returns Mutation to update a resource with optimistic updates
 */
export function useUpdateResource() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, type, body }) => updateRichContentResource(id, type, body),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ id, body }) {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({
                queryKey: TEMPLATE_RESOURCES_QUERY_KEY
            });
            // Snapshot the previous value
            const previousResources = queryClient.getQueryData(TEMPLATE_RESOURCES_QUERY_KEY);
            // Optimistically update to the new value
            if (previousResources) {
                queryClient.setQueryData(TEMPLATE_RESOURCES_QUERY_KEY, (oldResources) => (oldResources === null || oldResources === void 0 ? void 0 : oldResources.map((resource) => (resource.id === id ? Object.assign(Object.assign({}, resource), { body }) : resource))) || []);
            }
            // Return a context object with the snapshotted value
            return { previousResources };
        }),
        onError: (err, variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousResources) {
                queryClient.setQueryData(TEMPLATE_RESOURCES_QUERY_KEY, context.previousResources);
                toast.error(`Failed to update resource: ${err.message}`);
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure we have the latest data
            void queryClient.invalidateQueries({ queryKey: TEMPLATE_RESOURCES_QUERY_KEY });
        }
    });
}
